/**
 * ArticleCompanySetup.tsx — /company-setup-romania
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Same visual language as existing articles: Playfair Display headings, Source Sans 3 body
 * - Muted earth tones, generous whitespace, data tables for reference
 * - Hero image: to be provided by user (placeholder used for preview)
 */

import { useState } from "react";
import { Clock, Share2, ChevronRight, MapPin, ExternalLink, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Hero image — placeholder until user provides CDN URL
const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp";

// Related articles images
const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";

const RELATED = [
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
  { tag: "City Guide", title: "Brașov, straight up: beyond the bear photos and the mountain air clichés", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "Real Estate", title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing", img: ARTICLE3_IMG, href: "/buying-apartment-brasov" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full md:w-56 h-40 object-cover flex-shrink-0" loading="lazy" />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

// Cost table — two columns (one-time / recurring)
function CostTable({ rows }: { rows: { item: string; type: string; amount: string }[] }) {
  return (
    <div className="my-6 w-full" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table
        className="border-collapse"
        style={{ width: "100%", tableLayout: "fixed", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}
      >
        <colgroup>
          <col style={{ width: "50%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "30%" }} />
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
            <th className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Cost Item</th>
            <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Type</th>
            <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
              <td className="text-stone-700 border-b border-stone-100" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", lineHeight: 1.4, wordBreak: "break-word" }}>{row.item}</td>
              <td className="text-right text-stone-500 border-b border-stone-100 text-xs" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}>{row.type}</td>
              <td className="text-right text-stone-700 border-b border-stone-100 font-mono font-semibold" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}>{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ArticleCompanySetup() {
  return (
    <>
    <Helmet>
      <title>Company Setup Romania 2026: Complete Guide for Expats</title>
      <meta name="description" content="How to open a company in Romania in 2026: 1% Micro vs 16% SRL, real costs, timelines, banking, e-Factura, and how Romania compares to other EU countries." />
      <meta property="og:title" content="Company Setup Romania 2026: Complete Guide for Expats" />
      <meta property="og:description" content="How to open a company in Romania in 2026: 1% Micro vs 16% SRL, real costs, timelines, banking, e-Factura, and how Romania compares to other EU countries." />
      <meta property="og:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp" />
      <meta property="og:image:width" content="1400" />
      <meta property="og:image:height" content="863" />
      <meta property="og:image:alt" content="Modern office desk with laptop and tablet — company setup guide Romania 2026" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/company-setup-romania" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Company Setup Romania 2026: Complete Guide for Expats" />
      <meta name="twitter:description" content="How to open a company in Romania in 2026: 1% Micro vs 16% SRL, real costs, timelines, banking, e-Factura, and EU comparison." />
      <meta name="twitter:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp" />
      <meta name="keywords" content="company setup Romania 2026, open SRL Romania, Romanian company registration, micro enterprise Romania, 1% tax Romania, ONRC registration, e-Factura Romania, expat business Romania, Brașov company setup" />
      <link rel="canonical" href="https://brasovgateway.com/company-setup-romania" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/company-setup-romania#article",
              "headline": "Company setup in Romania in 2026: the complete guide for expats and foreign investors",
              "description": "How to open a Romanian SRL in 2026 — taxes, costs, timelines, banking, and e-Factura compliance. Real information, no legal jargon.",
              "image": "${HERO_IMG}",
              "datePublished": "2026-04-14",
              "dateModified": "2026-04-29",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/company-setup-romania",
              "inLanguage": "en",
              "articleSection": "Business Setup"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Company Setup in Romania 2026",
                  "item": "https://brasovgateway.com/company-setup-romania"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Hero image */}
      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="Company setup Romania — SRL registration guide for expats and foreign investors" className="w-full h-full object-cover" fetchPriority="high" width="1400" height="863" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Article header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  Business Setup
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>10 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                Company setup in Romania in 2026: the complete guide for expats and foreign investors
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Business &amp; Legal Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/company-setup-romania" title="Company Setup in Romania: The No-Nonsense Guide for Expats" />
              </div>
            </div>

            {/* Article body */}
            <div>
              {/* Lead paragraph */}
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Romania is still one of the most tax-efficient jurisdictions in the European Union, but the days of setting up a shell company for €100 and paying almost zero taxes are over. In 2026, the Romanian state wants real businesses, real substance, and digital compliance. If you are an <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>expat, a digital nomad, or an investor</Link> looking to open an SRL in Brașov or anywhere else in Romania, here is exactly what you need to know — without the legal jargon and without the sugar-coating.
              </p>

              {/* Quick Facts grid */}
              <div className="my-8 border border-stone-200 p-6" style={{ backgroundColor: "white" }}>
                <p className="text-xs uppercase tracking-widest font-semibold mb-5 text-stone-400">Quick Facts — Romanian SRL in 2026</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { label: "Timeframe", value: "5–14 working days", note: "State is fast. Banks are slow." },
                    { label: "Minimum Capital", value: "500 RON", note: "~€100" },
                    { label: "Micro Tax (under €100k)", value: "1% on revenue", note: "Requires 1 employee" },
                    { label: "Standard SRL Tax", value: "16% on profit", note: "No employee required" },
                    { label: "Dividend Tax", value: "16%", note: "On distributions to shareholders" },
                    { label: "VAT Rate", value: "21%", note: "Standard rate" },
                    { label: "Local Director", value: "Not required", note: "You can direct as a foreigner" },
                    { label: "e-Factura", value: "Mandatory", note: "All B2B invoices" },
                    { label: "Registered Office Hosting", value: "Mandatory", note: "Physical address required in Romania" },
                  ].map((fact, i) => (
                    <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                      <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                      <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                      <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Table of Contents */}
              <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>Table of Contents</p>
                <ol className="space-y-2">
                  {[
                    { n: "01", label: "The 2026 reality check: micro vs. standard SRL", anchor: "#micro-vs-standard" },
                    { n: "02", label: "Step-by-step: how to actually register a company", anchor: "#step-by-step" },
                    { n: "03", label: "The real costs of running a Romanian company", anchor: "#real-costs" },
                    { n: "04", label: "Banking in Romania as a foreigner (the real boss fight)", anchor: "#banking" },
                    { n: "05", label: "Accounting & e-Factura: welcome to digital bureaucracy", anchor: "#accounting" },
                    { n: "06", label: "Romania vs. other EU countries: how does it really compare?", anchor: "#eu-comparison" },
                  ].map((item) => (
                    <li key={item.n} className="flex items-start gap-3">
                      <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                      <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Section 1 */}
              <h2 id="micro-vs-standard" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The 2026 reality check: micro vs. standard SRL
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                If you read an article from 2022 telling you that Romania has a 1% tax rate with no strings attached, close that tab immediately. The rules have changed significantly. Today, you have two main paths when opening an SRL (Societate cu Răspundere Limitată), and which one applies to you depends on your revenue, your business model, and whether you are willing to put someone on payroll.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The Micro-Enterprise (1% on revenue)</strong> is the famous "Romanian tax haven" setup, but it now comes with strict conditions. To qualify, your company must have a yearly revenue of under €100,000, employ at least one full-time worker — which means paying social contributions of roughly €250–€300 per month — and not be involved in certain restricted activities. Some IT consulting structures, depending on the exact CAEN codes and ownership setup, may default to the 16% profit tax regime regardless of revenue.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The Standard SRL (16% on profit)</strong> applies if you do not want to hire an employee, or if your revenue exceeds €100,000. For many service-based expats with low operating expenses, 16% on net profit is still highly competitive compared to Western Europe — but it requires proper expense tracking and a good accountant. If you plan to hire non-EU workers for your Romanian company, note that the process is governed by a separate framework — see our guide on <Link href="/working-in-romania-foreigner-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>working in Romania as a foreigner under OUG 32/2026</Link>.
              </p>
              <div className="my-6 px-5 py-4 border-l-4" style={{ borderLeftColor: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-stone-600 text-sm font-light leading-relaxed">
                  <strong className="text-stone-800">Important:</strong> Regardless of which structure you choose, the dividend tax when you extract money to your personal account is <strong>16%</strong>. This applies on top of the corporate tax — it is not a substitute for it.
                </p>
              </div>

              {/* Section 2 */}
              <h2 id="step-by-step" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Step-by-step: how to actually register a company
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The actual incorporation process at the Trade Registry (ONRC) is surprisingly modern and can be done entirely online if you have a Romanian electronic signature. If you don't, you will need a lawyer or an agency to submit the file on your behalf — which is the case for most expats on their first registration.
              </p>

              {[
                {
                  step: "01",
                  title: "Name reservation",
                  text: "You propose up to three names. The Trade Registry checks availability and reserves your chosen name on the same day. The reservation is valid for 3 months. Avoid names that are too generic or that include restricted terms (\"national\", \"Romanian\", \"bank\")."
                },
                {
                  step: "02",
                  title: "Registered office (sediu social)",
                  text: "Your company needs a physical address in Romania — no P.O. Box. Options include renting an office, using your apartment (requires written consent from co-owners and sometimes the homeowners' association), or paying a law firm or agency to host your registered office for a yearly fee of €200–€300."
                },
                {
                  step: "03",
                  title: "Articles of incorporation",
                  text: "This document outlines ownership percentages, who the administrator is, and what your main business activity is — identified by a CAEN code. Choosing the right CAEN codes matters: it determines your tax regime eligibility and what activities you are legally allowed to invoice."
                },
                {
                  step: "04",
                  title: "Depositing the share capital",
                  text: "As of 2026, the minimum is 500 RON (approximately €100). You deposit this into a temporary bank account and bring the deposit receipt to the Trade Registry as part of your file."
                },
                {
                  step: "05",
                  title: "Submission to ONRC",
                  text: "Once the complete file is submitted, the Trade Registry typically processes it in 3 to 5 working days and issues the company registration certificate (Certificat de Înregistrare) along with your unique fiscal code (CUI)."
                },
                {
                  step: "06",
                  title: "ANAF registration",
                  text: "After ONRC, you register with the tax authority (ANAF) to activate your fiscal profile, declare your tax regime (Micro or Standard), and — if applicable — register for VAT. This step is often overlooked by first-time founders and can cause delays if not handled promptly."
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 mb-5 p-4 border border-stone-200" style={{ backgroundColor: i % 2 === 0 ? "white" : "oklch(0.985 0.006 75)" }}>
                  <div className="flex-shrink-0">
                    <span className="text-xs font-bold" style={{ color: "oklch(0.38 0.12 15)" }}>{item.step}</span>
                  </div>
                  <div>
                    <p className="text-stone-900 font-semibold text-sm mb-1">{item.title}</p>
                    <p className="text-stone-600 text-sm font-light leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}

              <p className="text-stone-600 font-light leading-relaxed mt-4 mb-6" style={{ lineHeight: "1.8" }}>
                Why do we say 14 working days in our service timeline? Because getting the company documents is only half the battle. Opening the operational bank account and completing the ANAF registration takes additional time — especially for non-residents navigating the process for the first time.
              </p>

              {/* Section 3 */}
              <h2 id="real-costs" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The real costs of running a Romanian company
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Setting up the company is cheap. Running it legally and compliantly requires a monthly budget. Below is a realistic breakdown of what you should expect to pay to keep your SRL operational in 2026.
              </p>

              <CostTable rows={[
                { item: "State registration fees (ONRC)", type: "One-time", amount: "~€30" },
                { item: "Share capital deposit", type: "One-time", amount: "500 RON (~€100)" },
                { item: "Agency or lawyer fee", type: "One-time", amount: "€500 – €600" },
                { item: "Accounting", type: "Monthly", amount: "€100 – €200 / mo" },
                { item: "Registered office hosting", type: "Yearly", amount: "€200 – €300 / yr" },
                { item: "Minimum wage employee (Micro regime)", type: "Monthly", amount: "~€250 – €300 / mo" },
                { item: "Bank account fees", type: "Monthly", amount: "€10 – €20 / mo" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The accounting line is not negotiable. Romanian tax legislation changes frequently — the micro-enterprise rules were overhauled in 2023, 2024, and again in 2026. A certified accountant (CECCAR member) who speaks English is not a luxury; it is the most important monthly expense your company will have. Budget for it from day one.
              </p>

              {/* Section 4 */}
              <h2 id="banking" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Banking in Romania as a foreigner (the real boss fight)
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The Trade Registry will give you your company papers in a few days. The bank might take three weeks to open your account. This is not an exaggeration.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Due to strict EU Anti-Money Laundering (AML) and Know Your Customer (KYC) regulations, Romanian banks are thorough — and sometimes slow — when dealing with newly formed companies owned by non-residents. If you walk into a branch in Brașov with a foreign passport and brand new company papers, expect a compliance review that includes questions about your business model, your clients, and the source of your funds.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Physical presence is almost always required. While you can incorporate remotely via a proxy, banks will want the company administrator in the branch to open the operational account. Come prepared with a clear, written description of your business — what you do, who your clients are, and why you chose Romania as your base. "Because the taxes are low" is not a sufficient answer. "I am relocating to Brașov and expanding my consultancy to serve Eastern European clients" is.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Among the major Romanian banks, Banca Transilvania, ING, and BCR generally have more structured onboarding processes for foreign directors, though compliance checks are mandatory everywhere. Some expats also use fintech alternatives (Revolut Business, Wise Business) as a bridge while the Romanian account is being processed — though note that these are not substitutes for a local RON account when it comes to tax payments to ANAF.
              </p>

              {/* Section 5 */}
              <h2 id="accounting" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Accounting &amp; e-Factura: welcome to digital bureaucracy
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Romania has aggressively digitized its tax collection infrastructure over the past two years, and the most significant change for business owners is <strong className="text-stone-800">RO e-Factura</strong>.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Every B2B invoice issued by a Romanian company must be uploaded to the state's electronic invoicing system (accessible via the SPV — Spațiul Privat Virtual portal) within 5 calendar days of issuance. You cannot simply write an invoice in Word, send it as a PDF, and consider the transaction done. The invoice must exist in the state's system first. Failure to comply results in fines equal to the value of the non-compliant invoice.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Your accountant will handle the e-Factura submissions as part of their monthly service. This is precisely why choosing an accountant who is fluent in both Romanian tax law and English is critical — you need to be able to communicate clearly about every transaction, every month, without ambiguity.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                For B2C transactions (selling to private individuals), e-Factura is not yet mandatory, but the Romanian government has signalled that it is coming. Build your processes around digital compliance from day one.
              </p>

              {/* Section 6 — EU Comparison */}
              <h2 id="eu-comparison" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Romania vs. other EU countries: how does it really compare?
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Romania is frequently mentioned in the same breath as Bulgaria, Estonia, and Hungary when expats discuss low-tax EU jurisdictions. The comparison is legitimate, but the details matter. Here is how Romania stacks up against the main alternatives — across the metrics that actually affect your decision.
              </p>

              <div className="my-6 w-full" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.6rem, 2vw, 0.8rem)" }}>
                  <colgroup>
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                      <th className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.4rem, 1.5vw, 0.75rem)", fontSize: "clamp(0.55rem, 1.6vw, 0.7rem)", letterSpacing: "0.04em" }}>Metric</th>
                      {["Romania", "Bulgaria", "Estonia", "Hungary", "Germany", "Netherlands"].map((c, i) => (
                        <th key={i} className={`text-right border-b border-stone-200 font-semibold uppercase tracking-wider`} style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.4rem, 1.5vw, 0.75rem)", fontSize: "clamp(0.55rem, 1.6vw, 0.7rem)", color: i === 0 ? "oklch(0.38 0.12 15)" : undefined, letterSpacing: "0.04em" }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { metric: "Corporate tax (standard)", values: ["16%", "10%", "22% (dist.)", "9–11%", "~30%", "19–25.8%"] },
                      { metric: "Micro / small co. rate", values: ["1% on rev.", "—", "0% retained", "—", "—", "—"] },
                      { metric: "Dividend tax", values: ["16%", "5%", "24% PIT", "15% PIT", "25% PIT", "15% PIT"] },
                      { metric: "Combined rate (owner-op.)", values: ["~29.4%", "15%", "~22%", "~24.5%", "~50%+", "~35%"] },
                      { metric: "Min. share capital", values: ["€100", "€1", "€0.01", "€100", "€25,000", "€1"] },
                      { metric: "Registration fee", values: ["~€150", "€28", "€265", "~€100", "€300–600", "€50–200"] },
                      { metric: "Time to register", values: ["3–5 days", "3–5 days", "1–3 days", "~1 week", "2–4 weeks", "1–4 weeks"] },
                      { metric: "Notary required", values: ["No", "No", "No", "Yes", "Yes", "Yes"] },
                      { metric: "Annual accounting cost", values: ["€1,200–3,000", "€1,200–2,400", "€2,400–4,800", "€1,800–3,600", "€3,000–6,000", "€3,000–6,000"] },
                      { metric: "e-Invoicing mandatory", values: ["Yes (B2B)", "No", "No", "No", "Partial", "Partial"] },
                    ].map((row, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                        <td className="text-stone-700 border-b border-stone-100 font-medium" style={{ padding: "clamp(0.4rem, 1.5vw, 0.65rem) clamp(0.4rem, 1.5vw, 0.75rem)", lineHeight: 1.4 }}>{row.metric}</td>
                        {row.values.map((v, j) => (
                          <td key={j} className="text-right border-b border-stone-100" style={{ padding: "clamp(0.4rem, 1.5vw, 0.65rem) clamp(0.4rem, 1.5vw, 0.75rem)", lineHeight: 1.4, fontWeight: j === 0 ? 600 : 400, color: j === 0 ? "oklch(0.38 0.12 15)" : "oklch(0.4 0 0)" }}>{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Where Romania wins.</strong> For a high-margin service business under €100,000 revenue — a consultant, a developer, a designer — the 1% micro-enterprise rate is genuinely unbeatable in the EU. No other jurisdiction taxes revenue at 1%. Bulgaria comes close on the combined rate (15% total), but that is still 15 times higher than Romania's micro rate on the same revenue figure. Germany and the Netherlands are not even in the same conversation for small owner-operators: a GmbH in Germany requires €25,000 in share capital, a notary, and a combined effective rate that can exceed 50% when personal income tax is factored in.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">Where Romania loses.</strong> Once you cross the €100,000 revenue threshold, the picture changes dramatically. Romania's combined rate of ~29.4% (16% CIT + 16% dividend) is higher than Bulgaria (15%), Estonia (22% at distribution), Hungary (~24.5%), and Lithuania (~21%). If your business is scaling, Bulgaria becomes the more rational choice purely on tax grounds. Romania also loses on compliance complexity: ANAF is more aggressive than Bulgaria's NRA, the legislation changes more frequently, and e-Factura adds a layer of operational overhead that no other low-tax EU country currently imposes.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The Brașov factor.</strong> Tax rates are only one variable. Romania — and Brașov specifically — offers something Bulgaria, Estonia, and Hungary do not: a city with genuine infrastructure, a growing expat community, direct flights to major European hubs, and <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>a cost of living that makes the after-tax income go further</Link>. For entrepreneurs who want to actually live where their company is registered, Brașov is a more compelling base than Sofia, Tallinn, or <Link href="/brasov-vs-budapest-prague-vienna-expats" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>Budapest on quality-of-life grounds</Link>. The tax efficiency is the bonus, not the whole story.
              </p>

              {/* Blockquote */}
              <blockquote className="my-10 px-6 py-5 border-l-4" style={{ borderLeftColor: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-stone-700 text-lg font-light italic leading-relaxed" style={{ lineHeight: "1.8" }}>
                  "Setting up a company in Romania is no longer a loophole — it is a legitimate strategic move for entrepreneurs who want a competitive tax base inside the EU, provided they are willing to play by the new digital rules."
                </p>
              </blockquote>

              {/* CTA box */}
              <div className="my-10 p-8 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Company Setup Service</p>
                <h3 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.4rem" }}>
                  Ready to start your business in Brașov?
                </h3>
                <p className="text-sm font-light leading-relaxed mb-5 opacity-80">
                  Navigating the Trade Registry, the banks, and ANAF is much easier with local experts on your side. We help expats set up their Romanian SRLs, secure a registered office, and connect with English-speaking accountants.
                </p>
                <Link href="/services/company-setup">
                  <button className="text-xs py-3 px-6 border border-white/40 hover:bg-white/10 transition-colors tracking-widest font-semibold">
                    EXPLORE COMPANY SETUP SERVICES →
                  </button>
                </Link>
              </div>
            </div>

            <FeaturedDeveloper />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-stone-200">
              {["Company Setup", "Romania SRL", "Taxes 2026", "Expat Business", "Brașov", "e-Factura", "ONRC"].map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 border border-stone-300 text-stone-500 hover:border-stone-500 hover:text-stone-700 transition-colors cursor-pointer">
                  {tag}
                </span>
              ))}
            </div>
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 flex flex-col gap-6">
              <SidebarAd />
              <div className="p-5 border border-stone-200 bg-white">
                <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                  About Brașov Gateway
                </h4>
                <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                  We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                </p>
                <a href="/#contact"
                  className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                  style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                  GET IN TOUCH
                </a>
              </div>
              <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                  Stay in the loop
                </h4>
                <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                  No spam. One email per week with what is actually happening in Brașov.
                </p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Company Setup */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Business services</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Opening a company in Romania?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We handle the full SRL registration process — from name reservation to ANAF registration.
                </p>
                <Link href="/services/company-setup">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    VIEW SERVICE
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* Related articles */}
        <div className="mt-20 pt-12 border-t border-stone-200">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
            <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {RELATED.map((post, i) => (
              <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                <div className="overflow-hidden mb-4">
                  <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{post.tag}</span>
                <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                  {post.title}
                </h3>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  READ MORE <ChevronRight size={11} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
